import axios from 'axios';
import Swal from 'sweetalert2';
import { msalInstance, loginRequest } from './msalInstance';
import config from '../config';

// The proxy refuses with 403 { error: "trial_expired" | "feature_not_enabled" |
// "org_inactive" } when the organization's plan doesn't cover a request. Pages
// don't each need to know about that — explain it once here, at most once per
// reason per minute, so a burst of failing calls doesn't stack dialogs.
const _explained = {};
export function explainPlanRefusal(body) {
  const reason = body && body.error;
  // A module outside the plan is explained by the page guard (RequireFeature);
  // background calls from pages that ARE allowed must not pop dialogs.
  if (reason === "feature_not_enabled") { console.warn("[plan] feature not enabled:", body.required); return true; }
  if (!["trial_expired", "org_inactive"].includes(reason)) return false;
  const now = Date.now();
  if (_explained[reason] && now - _explained[reason] < 60 * 1000) return true;
  _explained[reason] = now;
  const support = config.supportEmail;
  const copy = {
    trial_expired: {
      title: "Your trial has ended",
      text: `You can still view your data, but changes are no longer saved. Contact ${support} to continue with EcoPrism.`,
    },
    feature_not_enabled: {
      title: "Not included in your plan",
      text: `This module (${body.required || "requested"}) isn't part of your organization's EcoPrism plan. Contact your EcoPrism administrator or ${support}.`,
    },
    org_inactive: {
      title: "Organization deactivated",
      text: `This organization has been deactivated on EcoPrism. Contact ${support}.`,
    },
  }[reason];
  Swal.fire({ icon: "warning", ...copy, confirmButtonColor: "#407775" });
  return true;
}

async function inspectRefusal(response) {
  try {
    if (response && response.status === 403) {
      const body = await response.clone().json();
      explainPlanRefusal(body);
    }
  } catch (e) {
    // not JSON — nothing to explain
  }
  return response;
}

// MSAL's acquireTokenSilent() calls window.fetch internally (endpoint/token
// requests). Since we monkeypatch window.fetch below, those internal calls must
// bypass our token logic — otherwise patched fetch -> getAADIdToken ->
// acquireTokenSilent -> fetch -> ... recurses ("Maximum call stack size exceeded").
const MSAL_HOSTS = ['login.microsoftonline.com', 'login.windows.net', 'login.live.com'];
function isMsalEndpoint(url) {
  try {
    const u = typeof url === 'string' ? url : (url && url.url) || '';
    return MSAL_HOSTS.some((h) => u.includes(h));
  } catch (e) {
    return false;
  }
}

// Only attach the Bearer token to OUR backend (where the AAD middleware lives).
// Prevents leaking the idToken to third-party origins (news feeds, external APIs)
// that the app may fetch.
const API_BASE = (process.env.REACT_APP_BASE_API_URL || '').replace(/\/$/, '');
function isAppApi(url) {
  try {
    const u = typeof url === 'string' ? url : (url && url.url) || '';
    if (!u) return false;
    if (u.startsWith('/')) return true; // relative → same origin
    if (API_BASE && u.startsWith(API_BASE)) return true;
    if (u.startsWith(window.location.origin)) return true;
    return false;
  } catch (e) {
    return false;
  }
}

// Dedupe concurrent token acquisitions — many in-flight requests would otherwise
// each trigger acquireTokenSilent simultaneously.
let inFlightToken = null;

async function getAADIdToken() {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) return null;
  if (inFlightToken) return inFlightToken;

  // A browser can hold several MSAL accounts (work + other orgs). Always use
  // the one the user actually signed into THIS app with; sending a token for a
  // different account makes the proxy authorize the wrong identity (the gate
  // then overwrites the username header with that account and access checks
  // fail with confusing "User not provisioned" errors). No match -> no token.
  const sessionUser = (localStorage.getItem('userName') || '').toLowerCase();
  const account = accounts.find((a) => (a.username || '').toLowerCase() === sessionUser);
  if (!account) {
    console.warn('[authFetch] no MSAL account matches the signed-in user; not attaching a token');
    return null;
  }

  inFlightToken = (async () => {
    try {
      const result = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      // Store fresh token so LoginHeader expiry check stays valid
      localStorage.setItem('JWT', result.idToken);
      return result.idToken;
    } catch (err) {
      console.warn('[authFetch] silent token acquisition failed', err.message);
      return null;
    } finally {
      inFlightToken = null;
    }
  })();

  return inFlightToken;
}

export function installAuthInterceptor() {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (url, options = {}) => {
    // Only our backend gets the token; never MSAL's own requests (recursion).
    if (!isMsalEndpoint(url) && isAppApi(url) && localStorage.getItem('loginType') === 'AAD') {
      const token = await getAADIdToken();
      if (token) {
        const headers = new Headers(options.headers || {});
        headers.set('Authorization', `Bearer ${token}`);
        // Keep username header so any route that still reads it works
        const username = localStorage.getItem('userName');
        if (username) headers.set('username', username);
        options = { ...options, headers };
      }
    }
    const response = await originalFetch(url, options);
    return isAppApi(url) ? inspectRefusal(response) : response;
  };

  // axios uses XMLHttpRequest, not window.fetch — patch the shared default
  // instance so the 6 config screens (newconfig, neweditconfig, configuration,
  // editConfiguration, EditConfig) get the same AAD headers.
  axios.interceptors.request.use(async (axiosConfig) => {
    const target = (axiosConfig.baseURL || '') + (axiosConfig.url || '');
    if (isAppApi(target) && localStorage.getItem('loginType') === 'AAD') {
      const token = await getAADIdToken();
      if (token) {
        axiosConfig.headers = axiosConfig.headers || {};
        axiosConfig.headers.Authorization = `Bearer ${token}`;
        const username = localStorage.getItem('userName');
        if (username) axiosConfig.headers.username = username;
      }
    }
    return axiosConfig;
  });
  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      const target = error && error.config ? (error.config.baseURL || '') + (error.config.url || '') : '';
      if (error && error.response && error.response.status === 403 && isAppApi(target)) {
        explainPlanRefusal(error.response.data);
      }
      return Promise.reject(error);
    }
  );
}
